import { useContext, useEffect, useMemo, useState } from "react";

import { searchInsuranceOfferPractice } from "@datanac/datanac-api-toolkit/dist/utility/InsuranceOfferHelper";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ApiHelper as ApiHelperWrapper, RmaApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { formatPrice } from "utility/formatters";

import DataFormSelect from "../../Core/DataFormSelect";
import TilleyFormSelect from "../../Core/TilleyFormSelect";
import { ArrowForwardIcon, CloseIcon } from "../../Icons/Icons";
import { getBudgetYears } from "../helpers";

const MAX_DIALOG_WIDTH = 82.5;
const SIDEBAR_WIDTH = 23.75;

const mapPracticeNameForTemplate = (practiceName) => {
  const mappings = {
    "Non-Irrigated": "Non-Irrigated",
    Irrigated: "Irrigated",
    "Organic(Certified) Irr.": "Irrigated",
    "Organic(Certified) Non-Irr.": "Non-Irrigated",
    "Organic(Transitional) Irr.": "Irrigated",
    "Organic(Transitional) Non-Irr.": "Non-Irrigated",
  };

  return mappings[practiceName] || practiceName;
};

const BudgetTemplateDialog = ({ open, onClose, onSelectTemplate, state }) => {
  const { globalState } = useContext(AppContext);
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });

  const [selectedState, setSelectedState] = useState("");
  const [selectedCounty, setSelectedCounty] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedCommodity, setSelectedCommodity] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedPractice, setSelectedPractice] = useState("");

  const [allTemplates, setAllTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedState(state?.state_name || "");
      setSelectedYear(state?.year ? String(Number(state.year)) : "");
      setSelectedCounty(state?.county_name || "");
      setSelectedCommodity(state?.commodity_name || "");
      setSelectedPractice(state?.practice_name || "");
      setSelectedRegion("");
    }
  }, [open, state]);

  useEffect(() => {
    if (selectedYear && selectedState && selectedCommodity) {
      loadBudgetTemplates();
    } else {
      setAllTemplates([]);
    }
  }, [selectedYear, selectedState, selectedCommodity, selectedCounty, selectedPractice]);

  useEffect(() => {
    if (allTemplates.length > 0) {
      const filtered = selectedRegion ? allTemplates.filter((t) => t.area === selectedRegion) : allTemplates;

      setFilteredTemplates(filtered);
    } else {
      setFilteredTemplates([]);
    }
  }, [selectedRegion, allTemplates]);

  const loadBudgetTemplates = async () => {
    if (!selectedYear || !selectedState || !selectedCommodity) {
      setAllTemplates([]);
      return;
    }

    setIsLoading(true);

    try {
      const budgetParameters = {
        year: selectedYear,
        state_name: selectedState,
        commodity_name: selectedCommodity,
      };

      if (selectedCounty) {
        budgetParameters.county_name = selectedCounty;
      }

      if (selectedPractice) {
        const mappedPracticeName = mapPracticeNameForTemplate(selectedPractice);
        budgetParameters.practice_name = mappedPracticeName;
      }

      const templates = await ApiHelperWrapper.callSmociObject(
        "data/DATANAC",
        "FSA.Extension",
        "Budget",
        "Search",
        budgetParameters,
        globalState
      );
      setAllTemplates(templates || []);
    } catch (error) {
      console.error("ERROR loading templates:", error);
      setAllTemplates([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    onSelectTemplate(template);
    onClose();
  };

  const handleClose = () => {
    setSelectedState("");
    onClose();
  };

  const handleChangeState = (event, value) => {
    if (!event?.target) return;

    const eventName = event.target.name;
    const eventValue = value ?? event.target.value;

    switch (eventName) {
      case "year":
        setSelectedYear(eventValue || "");
        setSelectedRegion("");
        break;
      case "state_name":
        setSelectedState(eventValue || "");
        setSelectedCounty("");
        setSelectedRegion("");
        break;
      case "county_name":
        setSelectedCounty(eventValue || "");
        setSelectedRegion("");
        break;
      case "commodity_name":
        setSelectedCommodity(eventValue || "");
        setSelectedRegion("");
        break;
      case "practice_name":
        setSelectedPractice(eventValue || "");
        setSelectedRegion("");
        break;
      case "region":
        setSelectedRegion(eventValue || "");
        break;
      default:
        console.warn(`Unhandled event name: ${eventName}`);
        break;
    }
  };

  const commodityDataFormSelect = useMemo(() => {
    return (
      <DataFormSelect
        value={selectedCommodity || ""}
        name="commodity_name"
        onChange={handleChangeState}
        objectNameLabel="Commodity"
        itemGetKey={(item) => item.commodity_name}
        itemGetLabel={(item) => item.commodity_name}
        doFetch={selectedState && selectedCounty}
        doAllowEmptyElement={true}
        isRequired={true}
        customDataLoader={() =>
          RmaApiHelper.searchInsuranceOfferCommodity(
            {
              state_name: selectedState,
              county_name: selectedCounty,
            },
            globalState
          )
        }
      />
    );
  }, [selectedState, selectedCounty, selectedCommodity]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      disableScrollLock={false}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: `${MAX_DIALOG_WIDTH}rem !important`,
          margin: { xs: "1rem !important" },
          height: "auto",
          maxHeight: {
            xs: "90vh",
            lg: "80vh",
          },
          minHeight: {
            xs: "90vh",
            lg: "80vh",
            xl: "unset",
          },
          borderRadius: "0.875rem",
          backgroundColor: "background.default",
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "18px 26px 0",
          backgroundColor: "background.default",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "700 !important", color: "text.primary" }}>
          Budget Templates
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: "text.primary", right: "-1rem", top: "-0.5rem" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: { xs: "1rem !important", md: "1.5rem !important" },
          flex: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          backgroundColor: "transparent",
          overflowY: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        {/* Left Sidebar - State Filter */}
        <Box
          sx={{
            width: { xs: "100%", md: `${SIDEBAR_WIDTH}rem` },
            maxWidth: { md: `${SIDEBAR_WIDTH}rem` },
            minWidth: { md: `${SIDEBAR_WIDTH}rem` },
            flex: { xs: "0 0 auto", md: "1 1 0" },
            minHeight: { xs: "auto", md: 0 },
            p: { xs: 2, md: 3 },
            backgroundColor: "background.paper",
            borderRadius: "0.5rem",
            boxSizing: "border-box",
            flexShrink: 0,
            display: "flex",
            gap: 1,
            flexDirection: "column",
            ...(isSmall
              ? {}
              : {
                  overflowY: "auto",
                }),
          }}
        >
          <TilleyFormSelect
            name="year"
            value={selectedYear || ""}
            onChange={handleChangeState}
            objectName="BudgetYear"
            objectNameLabel="Year"
            query={async () => {
              const years = getBudgetYears();
              return years.map((year) => ({ year: String(year), key: String(year), value: String(year) }));
            }}
            itemGetKey={(item) => String(item.year)}
            itemGetLabel={(item) => String(item.year)}
            doFetch={true}
            isRequired={true}
            doAllowEmptyElement={true}
          />

          <DataFormSelect
            name="state_name"
            value={selectedState || ""}
            onChange={handleChangeState}
            objectName="State"
            objectNameLabel="State"
            itemFilter={{ reinsurance_year: selectedYear }}
            itemGetKey={(item) => item.state_name}
            itemGetLabel={(item) => item.state_name}
            doFetch={selectedYear}
            isRequired={true}
            doAllowEmptyElement={true}
          />

          <DataFormSelect
            name="county_name"
            value={selectedCounty || ""}
            onChange={handleChangeState}
            objectName="County"
            objectNameLabel="County"
            itemFilter={{
              reinsurance_year: selectedYear,
              state_name: selectedState,
            }}
            itemGetKey={(item) => item.county_name}
            itemGetLabel={(item) => item.county_name}
            doFetch={selectedYear && selectedState}
            doAllowEmptyElement={true}
          />

          <TilleyFormSelect
            name="region"
            value={selectedRegion || ""}
            onChange={handleChangeState}
            objectName="Region"
            objectNameLabel="Region"
            query={async () => {
              const uniqueRegions = [...new Set(allTemplates.map((t) => t.area).filter(Boolean))];
              return uniqueRegions.map((area) => ({ area, key: area, value: area }));
            }}
            itemFilter={{}}
            itemGetKey={(item) => item.area}
            itemGetLabel={(item) => item.area}
            doFetch={allTemplates.length > 0}
            doAllowEmptyElement={true}
          />

          {/* Separator */}
          <Divider sx={{ my: 1 }} />

          {commodityDataFormSelect}

          <DataFormSelect
            name="practice_name"
            value={selectedPractice || ""}
            onChange={handleChangeState}
            objectNameLabel="Practice"
            itemGetKey={(item) => item.practice_name}
            itemGetLabel={(item) => item.practice_name}
            doFetch={selectedState && selectedCounty && selectedCommodity}
            doAllowEmptyElement={true}
            customDataLoader={() =>
              searchInsuranceOfferPractice(
                {
                  state_name: selectedState,
                  county_name: selectedCounty,
                  commodity_name: selectedCommodity,
                },
                RmaApiHelper,
                selectedYear
              )
            }
          />
        </Box>

        <Divider
          orientation={isSmall ? "horizontal" : "vertical"}
          flexItem
          sx={{
            margin: { xs: 0, md: "24px !important" },
          }}
        />

        {/* Right Content - Results */}
        <Box
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            width: { xs: "100%", md: "auto" },
            ...(isSmall
              ? {}
              : {
                  overflow: "hidden",
                  minHeight: 0,
                }),
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              ...(isSmall ? {} : { overflow: "auto" }),
            }}
          >
            {!selectedYear || !selectedState || !selectedCommodity ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  p: 3,
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h6" sx={{ mb: 1, color: "text.secondary" }}>
                    Select required fields
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please select Year, State, and Commodity to search for budget templates.
                  </Typography>
                </Box>
              </Box>
            ) : filteredTemplates.length === 0 && !isLoading ? (
              /* Empty State */
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  p: 3,
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="h6" sx={{ mb: 1, color: "text.secondary" }}>
                    Sorry, nothing found.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No budget templates available for the selected criteria. Try adjusting your filters.
                  </Typography>
                </Box>
              </Box>
            ) : (
              /* Budget Templates List */
              <Box sx={{ height: "100%", overflow: "auto" }}>
                {isLoading ? (
                  <Box sx={{ height: "100%", minHeight: "7rem", textAlign: "center", alignContent: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <List sx={{ p: 0, marginRight: "6px !important" }}>
                    {filteredTemplates.map((template, index) => (
                      <ListItem
                        key={`${template.state_name}-${template.area}-${template.practice_name}-${template.name}-${index}`}
                        sx={{ p: 0, marginBottom: "8px !important" }}
                      >
                        <ListItemButton
                          disableRipple
                          onClick={isSmall ? () => handleTemplateSelect(template) : undefined}
                          data-testid={`template-item-${template.state_name}-${template.area}-${template.practice_name}`}
                          sx={{
                            backgroundColor: "background.paper",
                            borderRadius: "0.875rem",
                            cursor: "default",
                            display: "flex",
                            ...(isSmall
                              ? {
                                  p: "10px 12px",
                                  flexDirection: "column",
                                  alignItems: "end",
                                }
                              : {
                                  p: "16px 20px",
                                  alignItems: "center",
                                  gap: 2,
                                }),
                            "&:hover": {
                              backgroundColor: "primary.main",
                              color: "white",
                              "& .template-price": {
                                color: "white",
                              },
                              "& .template-arrow": {
                                color: "white",
                              },
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: "600 !important",
                              flex: "1",
                              userSelect: "text",
                              wordBreak: "break-word",
                              ...(!isSmall && {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }),
                            }}
                          >
                            {template.state_name}, {template.area} - {template.practice_name} - {template.name}
                          </Typography>

                          {isSmall ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mt: 1,
                              }}
                            >
                              <Typography variant="h6" className="template-price" color="primary.main">
                                {formatPrice(template.total)}
                              </Typography>
                              <ArrowForwardIcon className="template-arrow" sx={{ ml: 1 }} />
                            </Box>
                          ) : (
                            <>
                              <Typography
                                variant="h6"
                                color="primary.main"
                                className="template-price"
                                sx={{
                                  minWidth: "5rem",
                                  textAlign: "right",
                                  userSelect: "text",
                                }}
                              >
                                {formatPrice(template.total)}
                              </Typography>
                              <ArrowForwardIcon
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTemplateSelect(template);
                                }}
                                className="template-arrow"
                                data-testid={`template-arrow-${template.state_name}-${template.area}-${template.practice_name}`}
                                sx={{ cursor: "pointer" }}
                              />
                            </>
                          )}
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BudgetTemplateDialog;
